import PropTypes from "prop-types";

import { Pageblock } from "@components/pageblock";
import { NextHead } from "@components/nextHead";
import { Ad } from "@components/ad";
import { LatestArticles } from "@components/latestArticles";
import { SectionHeading } from "@components/headings/section";
import { Carousel } from "@components/carousel";
import { BasicCard } from "@components/cards/basic";
import { Eyebrow } from "@components/eyebrow";
import { Button } from "@components/button";
import { SocialMedia } from "@components/socialMedia";
import { ElsewhereDriver } from "@components/elsewhereDriver/elsewhereDriver";
import { NewsletterWidget } from "@components/newsletter/newsletterWidget";
import { BITPromo } from "@pageComponents/best-in-travel-2025/bitPromo";

import { tagular } from "@cohesion/tagular";
import { keyify } from "@utils/keyify";
import { dangerHTML } from "@utils/danger";
import { makeLpTitle } from "@utils/makeLpTitle";
import homepageAPI from "@queries/homepage/api";
import { LP_URL } from "@constants/lpUrl";
import { ImgixNextImage } from "@components/imgixNextImage";

export default function Homepage({ data }) {
  const { title, metaTags, latestStories, travelPicks, instagrams } = data;

  const trendingDestinationName = travelPicks.destinations.find(
    place =>
      place.badges && place.badges.some(badge => badge.slug === "trending")
  )?.name;

  const [primary, ...additional] = latestStories || [];

  return (
    <div>
      <NextHead metadata={metaTags}>
        <title>{makeLpTitle(title)}</title>
        <link key="canonical" rel="canonical" href={LP_URL} />
        <meta key="og:url" property="og:url" content={LP_URL} />

        <script
          type="application/ld+json"
          {...dangerHTML({
            __html: JSON.stringify({
              "@context": "http://www.schema.org",
              "@type": "Organization",
              name: "Lonely Planet",
              url: LP_URL,
              sameAs: [
                "https://www.instagram.com/lonelyplanet/",
                "https://www.facebook.com/lonelyplanet",
                "https://www.twitter.com/lonelyplanet",
                "https://www.tiktok.com/@lonelyplanet",
                "https://www.youtube.com/lonelyplanet",
                "https://www.pinterest.com/lonelyplanet/",
                "https://www.linkedin.com/company/lonely-planet",
              ],
              logo: "https://lonelyplanetstatic.imgix.net/marketing/2022/lp_logo_google.png",
            }),
          })}
        />

        <script
          type="application/ld+json"
          {...dangerHTML({
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "WebSite",
              url: LP_URL,
              potentialAction: {
                "@type": "SearchAction",
                target: `${LP_URL}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            }),
          })}
        />
      </NextHead>

      <section className="relative bg-[#F4D3CE]">
        <div className="container lg:flex lg:justify-between">
          <div className="max-w-[526px] mx-auto lg:ml-0 pt-6 lg:py-24 mb-4">
            <p className="uppercase label-sm mb-4">Lonely Planet Presents</p>
            <p className="heading-02 mb-6 text-pretty max-w-[450px]">
              The Ultimate Guide to Summer
            </p>
            <p>
              We&apos;re your very own super-powered hub for anything and
              everything summer: a day at the beach (or lake or river); getting
              outdoors for a hike or a bike or a walk; drinking and eating;
              choosing ideas for where to go and recommendations for last-minute
              getaways.
            </p>
            <Button
              className="block w-content mx-auto lg:ml-0 btn btn-primary mt-6"
              href="/campaigns/ultimate-guide-to-summer"
              onClick={() =>
                tagular("click", {
                  actionOutcome: "INTERNALLINK",
                  outboundUrl: "/campaigns/ultimate-guide-to-summer",
                  webElement: {
                    location: "HOMEPAGE HERO",
                    elementType: "BUTTON",
                    position: "1",
                    text: "Explore Summer",
                    name: "ULTIMATE GUIDE TO SUMMER",
                  },
                })
              }
            >
              Explore Summer
            </Button>
          </div>
          <div className="overflow-hidden self-end md:w-content">
            <ImgixNextImage
              className="hidden md:block translate-x-1 -mt-1 -mb-1"
              src="https://lonelyplanetstatic.imgix.net/marketing/2025/homepage/gts-hero-desktop.jpg"
              alt="The Ultimate Guide to Summer"
              width={650}
              height={720}
            />
            <ImgixNextImage
              className="md:hidden -mt-1 mx-auto"
              src="https://lonelyplanetstatic.imgix.net/marketing/2025/homepage/gts-hero-mobile.jpg"
              alt="The Ultimate Guide to Summer"
              width={330}
              height={410}
            />
          </div>
        </div>
      </section>

      <Pageblock>
        <div className="container">
          <Eyebrow>Plan your trip</Eyebrow>
          <div className="flex items-center justify-between">
            <SectionHeading className="mb-8 lg:mb-16">
              {travelPicks.sectionTitle}
            </SectionHeading>

            <Button
              href="/places"
              className="flex-shrink-0 hidden mb-4 lg:inline-block"
              onClick={() =>
                tagular("click", {
                  actionOutcome: "INTERNALLINK",
                  outboundUrl: "/places",
                  webElement: {
                    location: "SECTION",
                    elementType: "LINK",
                    position: "TRAVELPICKS",
                    text: "VIEWALLDESTINATIONS",
                  },
                })
              }
            >
              View all destinations
            </Button>
          </div>
        </div>

        <Carousel className="mb-8">
          {travelPicks.destinations.map(({ name, image, slug }, id) => (
            <BasicCard
              key={keyify(name)}
              type="destination"
              href={`/destinations/${slug}`}
              image={{ url: image }}
              title={name}
              taxonomy={
                name === trendingDestinationName
                  ? "🔥 Trending destination"
                  : null
              }
              onClick={() =>
                tagular("click", {
                  actionOutcome: "INTERNALLINK",
                  outboundUrl: `/${slug}`,
                  webElement: {
                    location: "CARDS",
                    elementType: "LINK",
                    position: "TRAVELPICKS",
                    text: `CARD${id + 1}`,
                  },
                })
              }
            />
          ))}
        </Carousel>

        <Button
          href="/places"
          className="block mx-auto lg:hidden w-content"
          onClick={() =>
            tagular("click", {
              actionOutcome: "ANCHOR",
              outboundUrl: "/places",
              webElement: {
                location: "SECTION",
                elementType: "LINK",
                position: "TRAVELPICKS",
                text: "VIEWALLDESTINATIONS",
              },
            })
          }
        >
          View all destinations
        </Button>
      </Pageblock>

      <Pageblock>
        <Ad placement="fullWidth" path="homepage/top" />
      </Pageblock>

      <NewsletterWidget
        stackedLayout
        topographyBackground
        formId="Homepage-banner"
      />

      <BITPromo />

      <ElsewhereDriver />

      <Pageblock>
        <LatestArticles
          eyebrow="Travel stories and news"
          title="Explore our latest stories"
          articles={{
            primary,
            additional,
          }}
          enableFilters={false}
          showNewsLink
        />
      </Pageblock>

      <Pageblock flush inset className="bg-blue">
        <SocialMedia promos={instagrams} />
      </Pageblock>
    </div>
  );
}

export async function getServerSideProps({ req, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=120, must-revalidate");
  const data = await homepageAPI(req, res);
  return {
    props: {
      data,
    },
  };
}

Homepage.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    metaTags: PropTypes.arrayOf(PropTypes.shape({})),
    travelPicks: PropTypes.shape({
      destinations: PropTypes.arrayOf(PropTypes.shape({})),
      sectionTitle: PropTypes.string,
    }),
    latestStories: PropTypes.arrayOf(PropTypes.shape({})),
    instagrams: PropTypes.arrayOf(
      PropTypes.shape({
        userName: PropTypes.string,
        profileUrl: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
      })
    ).isRequired,
  }).isRequired,
};
